import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"

import CheckboxList from "components/Checkbox/CheckboxList"
import FileUploaderV2 from "components/FileUploaderV2"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiAsyncAutoComplete from "components/MuiAsyncAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { DropdownObj } from "pages/Common/commonTypes"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import ChargerTable from "./ChargerTable"
import useInfra, { EInfraFields } from "./useInfra"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/sharp-solid-svg-icons"

const Infra = (props: ProjectFormPageProps) => {
    const { states, functions } = useInfra(props)
    const { infraRules } = useTemplateUIRules()

    const isScopeEditable = infraRules.scope_editable !== false
    const showEvaFields = infraRules.eva_fields_visible !== false
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                {/* Infra Scope of Work Section */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box display={"flex"} flexDirection={"column"} gap={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography
                                fontSize={"14px"}
                                variant="label"
                                textTransform={"none"}
                                display={"flex"}
                                alignItems={"center"}
                                gap={"4px"}>
                                Infra Scope of Work?*
                                {functions.hasError(EInfraFields.INFRA_SCOPE_OF_WORK) && (
                                    <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" size="sm" />
                                )}
                            </Typography>
                            {states.infraSOWOptions?.length > 0 && (
                                <Box display="flex" alignItems="center">
                                    <MuiCheckbox
                                        size="small"
                                        checked={states.isAllSOWSelected}
                                        onChange={e => functions.handleSelectAllSOW(e.target.checked)}
                                        sx={{
                                            marginRight: 0,
                                            marginBottom: "2px",
                                        }}
                                    />
                                    <Typography fontSize={"13px"} variant="label" fontWeight={400}>
                                        Select All
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {states.infraSowsIsFetching ? (
                            <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                        ) : (
                            <CheckboxList
                                name="infra_scope_of_work"
                                options={states.infraSOWOptions}
                                onChange={value => {
                                    states.formik.setFieldValue(EInfraFields.INFRA_SCOPE_OF_WORK, value)
                                }}
                                highlightChecked={true}
                                initialValues={states.formik.values[EInfraFields.INFRA_SCOPE_OF_WORK]}
                                disabled={!isScopeEditable}
                            />
                        )}
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                        <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                            Link Survey Ticket ID
                        </Typography>
                        <MuiAutoComplete
                            disablePortal={false}
                            size="large"
                            placeholder="Select Survey"
                            options={states.surveyOptions}
                            onChange={value => {
                                states.formik.setFieldValue(EInfraFields.SURVEY_TICKET_ID, value)
                            }}
                            value={states.formik.values[EInfraFields.SURVEY_TICKET_ID]}
                        />
                    </Box>
                </Box>

                {/* Infra Details Section */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                        INFRA DETAILS
                    </Typography>
                    <Box display={"flex"} flexDirection={"column"} gap={1}>
                        <Typography
                            fontSize={"14px"}
                            variant="label"
                            textTransform={"none"}
                            display={"flex"}
                            alignItems={"center"}
                            gap={"4px"}>
                            Is Material needed?*
                            {functions.hasError(EInfraFields.IS_MATERIAL_NEEDED) && (
                                <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" size="sm" />
                            )}
                        </Typography>
                        <RadioGroup
                            options={states.yesNoOptions}
                            name="is_material_needed"
                            onSelect={(value: DropdownObj) => {
                                states.formik.setFieldValue(EInfraFields.IS_MATERIAL_NEEDED, value)
                            }}
                            value={states.formik.values[EInfraFields.IS_MATERIAL_NEEDED]}
                        />
                    </Box>
                    {states.isCommissioningSelected && (
                        <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                            <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                <Typography variant="label">OCPP URL*</Typography>
                                <MuiTextField
                                    placeholder="Enter OCPP URL"
                                    size="large"
                                    name={EInfraFields.OCPP_URL}
                                    value={states.formik.values[EInfraFields.OCPP_URL]}
                                    onChange={states.formik.handleChange}
                                    onBlur={states.formik.handleBlur}
                                    error={functions.hasError(EInfraFields.OCPP_URL)}
                                />
                            </Box>
                            <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                <Typography variant="label">Port Number</Typography>
                                <MuiTextField
                                    placeholder="Enter Port Number"
                                    size="large"
                                    type="number"
                                    name={EInfraFields.PORT_NUMBER}
                                    value={states.formik.values[EInfraFields.PORT_NUMBER]}
                                    onChange={states.formik.handleChange}
                                    onBlur={states.formik.handleBlur}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Charger Section */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <Typography fontSize={"14px"} variant="label" color={"#101A41"}>
                            CHARGER ({states.chargerTableRows.length})
                        </Typography>
                        <Typography
                            fontSize={"10px"}
                            onClick={() => functions.setShowMore(!states.showMore)}
                            variant="label"
                            fontWeight={"300"}
                            textTransform={"none"}
                            sx={{
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                                cursor: "pointer",
                            }}>
                            See {states.showMore ? "Less" : "More"}
                        </Typography>
                    </Box>
                    {states.showMore && (
                        <>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography
                                    fontSize={"14px"}
                                    variant="label"
                                    textTransform={"none"}
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap={"4px"}>
                                    Are charger(s) supplied by Statiq?*
                                    {functions.hasError(EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ) && (
                                        <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" size="sm" />
                                    )}
                                </Typography>
                                <RadioGroup
                                    options={states.statiqSupplyOptions}
                                    name="are_chargers_supplied_by_statiq"
                                    onSelect={(value: DropdownObj) => {
                                        states.formik.setFieldValue(EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ, value)
                                    }}
                                    value={states.formik.values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ]}
                                />
                            </Box>

                            {/* Show Add chargers from Projects section when Statiq supplied and no existing charger data */}
                            {states.showAddChargersFromProjects && (
                                <Box
                                    bgcolor="#FAFAFA"
                                    borderRadius={"8px"}
                                    padding={"12px"}
                                    gap={2}
                                    display={"flex"}
                                    flexDirection={"column"}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Add chargers from
                                    </Typography>
                                    <RadioGroup
                                        options={states.addChargersFromOptions}
                                        name="add_chargers_from"
                                        onSelect={(value: DropdownObj) => {
                                            functions.handleAddChargersFromChange(value)
                                        }}
                                        value={states.addChargersFrom}
                                    />
                                    {states.addChargersFrom && (
                                        <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                            <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                                <Typography variant="label" textTransform={"none"}>
                                                    {states.isSiteSelected ? "Select Site" : "Select Project"}
                                                </Typography>
                                                {states.isSiteSelected ? (
                                                    <MuiAutoComplete
                                                        disablePortal={false}
                                                        placeholder="Select Site"
                                                        options={states.destinationSiteOption ? [states.destinationSiteOption] : []}
                                                        size="large"
                                                        value={states.destinationSiteOption}
                                                        disabled
                                                    />
                                                ) : (
                                                    <MuiAsyncAutoComplete
                                                        disablePortal={false}
                                                        placeholder="Select Project"
                                                        trigger={states.dispatchedProjectsTrigger}
                                                        buildQueryArg={({ page, page_size, search }) => ({ page, page_size, search })}
                                                        selectData={res => ({ options: res.data.map((p: any) => ({ id: p.project_id, title: `${p.project_name} (${p.project_display_id})` })), hasMore: res.have_more })}
                                                        size="large"
                                                        value={states.formik.values[EInfraFields.SELECT_PROJECT]}
                                                        onChange={value => functions.handleProjectChange(value)}
                                                    />
                                                )}
                                            </Box>
                                            <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                                <Typography variant="label" textTransform={"none"}>
                                                    Select chargers to add
                                                </Typography>
                                                <MuiAutoComplete
                                                    disablePortal={false}
                                                    placeholder="Select Chargers"
                                                    options={
                                                        states.isSiteSelected
                                                            ? states.selectedSiteChargerOptions
                                                            : states.selectedProjectChargerOptions
                                                    }
                                                    size="large"
                                                    multiple
                                                    loading={
                                                        states.isSiteSelected
                                                            ? states.isSiteChargersFetching
                                                            : states.isSelectedProjectChargersFetching
                                                    }
                                                    value={states.formik.values[EInfraFields.SELECT_CHARGERS_TO_ADD]}
                                                    onChange={value =>
                                                        states.formik.setFieldValue(
                                                            EInfraFields.SELECT_CHARGERS_TO_ADD,
                                                            value
                                                        )
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                    <Box display={"flex"} justifyContent={"flex-end"}>
                                        <Button
                                            variant="outlined"
                                            color="statiqOrange"
                                            onClick={functions.handleAddSelectedChargers}
                                            disabled={
                                                states.formik.values[EInfraFields.SELECT_CHARGERS_TO_ADD].length === 0
                                            }
                                            sx={{
                                                width: "100px",
                                            }}>
                                            ADD
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* Charger Table - shown when chargers supplied by Statiq is selected (Yes or No) */}
                            {states.formik.values[EInfraFields.ARE_CHARGERS_SUPPLIED_BY_STATIQ] && (
                                <Box borderRadius={"8px"} gap={2} display={"flex"} flexDirection={"column"}>
                                    <ChargerTable
                                        chargers={states.chargerTableRows}
                                        isStatiqSupplied={states.isStatiqSupplied}
                                        products={states.chargerProducts}
                                        onChargerChange={functions.handleChargerChange}
                                        onAddCharger={functions.handleAddCharger}
                                        onDeleteCharger={functions.handleDeleteCharger}
                                        onSkuChange={functions.handleSkuChange}
                                        onToggleCharger={functions.handleToggleCharger}
                                        showStatusColumns={states.showAddChargersFromProjects}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                {/* EV Application Section - Only show when Installation or Electrical Work scope is selected and EVA fields are visible */}
                {showEvaFields && states.isInstallationOrElectricalSelected && (
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        gap={2}
                        borderBottom={"1px solid #0000001A"}
                        paddingBottom={"12px"}>
                        <Box display={"flex"}>
                            <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                                EV APPLICATION
                            </Typography>
                        </Box>
                        <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                            <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Is EVA Required?
                                </Typography>
                                <RadioGroup
                                    options={states.yesNoOptions}
                                    name="is_eva_required"
                                    onSelect={(value: DropdownObj) => {
                                        states.formik.setFieldValue(EInfraFields.IS_EVA_REQUIRED, value)
                                    }}
                                    value={states.formik.values[EInfraFields.IS_EVA_REQUIRED]}
                                />
                            </Box>
                            {states.isEvaRequired && (
                                <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        EV Connection Owned by*
                                    </Typography>
                                    <RadioGroup
                                        options={states.evConnectionOwnedByOptions}
                                        name="ev_connection_owned_by"
                                        onSelect={(value: DropdownObj) => {
                                            states.formik.setFieldValue(EInfraFields.EV_CONNECTION_OWNED_BY, value)
                                        }}
                                        value={states.formik.values[EInfraFields.EV_CONNECTION_OWNED_BY]}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                <Box display={"flex"} flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                    <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                        <Typography fontSize={"14px"} variant="label">
                            Remarks
                        </Typography>
                        <MuiTextField
                            placeholder="Enter Remarks"
                            size="large"
                            name={EInfraFields.REMARKS}
                            value={states.formik.values[EInfraFields.REMARKS]}
                            onChange={states.formik.handleChange}
                            onBlur={states.formik.handleBlur}
                        />
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} gap={1} flex={1}>
                        <Typography fontSize={"14px"} variant="label">
                            Upload Document
                        </Typography>
                        <FileUploaderV2
                            maxFiles={1}
                            docIds={
                                states.existingInfraDocuments.length > 0
                                    ? states.existingInfraDocuments.map(doc => doc.doc_id)
                                    : undefined
                            }
                            key={`${states.existingInfraDocuments.length}-${states.project_id}`}
                            associatedId={states.project_id}
                            fileType={states.FILE_TYPES.INFRA_DOCUMENT}
                            onUpload={functions.handleFileUpload}
                            initialFileCount={states.existingInfraDocuments.length}
                            existingFileNames={states.existingInfraDocuments.map(doc => doc.document_title)}
                        />
                    </Box>
                </Box>
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={states.isLoading}
                handleSubmit={() => states.formik.handleSubmit()}
            />
        </>
    )
}

export default Infra
